import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';
import useDeliveries from '../hooks/useDeliveries';
import theme from 'App.theme';
import DeliveryList from 'components/DeliveryListPage/components/DeliveryList/DeliveryList';
import { CheckInDeliveryDialog } from 'components/CheckInDeliveryDialog/CheckInDeliveryDialog';
import { useDeliveryClickHandler } from 'hooks/useDeliveryClickHandler';
import { ActionItem, DeliveriesActionItemData } from '../types';
import useScreenSize from 'hooks/useScreenSize';
import AllClearMessage from './AllClearMessage';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { useIsTierZero } from 'hooks/useSubscriptionTier';
import FeatureUnavailableNotice from 'components/FeatureUnavailableNotice';

type Props = {
  actionItem: ActionItem;
  data?: DeliveriesActionItemData | null;
};

const DeliveriesActionItem: FC<Props> = ({ actionItem, data }) => {
  const isTierZero = useIsTierZero();

  const {
    data: deliveries,
    isLoading,
    count,
    total,
    search,
  } = useDeliveries({
    filters: actionItem.filters,
    data,
    disabled: isTierZero,
  });

  const { activeDeliveryForCheckInDeliveryDialog, handleDeliveryClick, setActiveDeliveryForCheckInDeliveryDialog } =
    useDeliveryClickHandler();

  const navigate = useNavigate();

  const handleRowClick = useCallback(
    async (deliveryId: number) => {
      const delivery = deliveries?.result.find(deliverySummary => deliverySummary.deliveryId === deliveryId);

      if (!delivery) {
        throw new Error('Delivery not found');
      }

      handleDeliveryClick(deliveryId, delivery.status, !delivery.surgeryReference || !delivery.patientInitials);
    },
    [handleDeliveryClick, deliveries?.result],
  );

  // a ref to get the page offset of the delivery list
  const qtyBannerRef = useRef<HTMLDivElement | null>(null);
  const screenSize = useScreenSize();
  const [deliveryListOffsetTop, setDeliveryListOffsetTop] = useState(0);

  // Use useEffect to calculate the offset
  useEffect(() => {
    if (!qtyBannerRef.current || isLoading) return;

    setDeliveryListOffsetTop(qtyBannerRef.current.getBoundingClientRect().bottom);
  }, [screenSize, isLoading, qtyBannerRef]);

  const pageSize = count || 0;
  const pageSizeOptions = useMemo(() => [pageSize], [pageSize]);

  const haveData = !!count;

  if (isTierZero) return <FeatureUnavailableNotice />;

  if (isLoading && !haveData) return <PageSpinner color={actionItem.colors.foreground} />;

  if (!haveData && !search.length)
    return (
      <AllClearMessage
        text={actionItem.lang.allClear}
        color={actionItem.colors.foreground}
        offsetTop={deliveryListOffsetTop}
      />
    );

  return (
    <>
      <Box
        ref={qtyBannerRef}
        sx={{
          color: theme.colors.white,
          backgroundColor: actionItem.colors.foreground,
          //border: `1px solid ${actionItem.colors.foreground}`,
          //borderBottomWidth: 0,
          borderTopLeftRadius: theme.layout.spacing.small,
          borderTopRightRadius: theme.layout.spacing.small,
          display: 'flex',
          alignItems: 'center',
          fontWeight: 'medium',

          // these fields are all required for in the show animation
          height: '48px',
          maxHeight: 0,
          minHeight: isLoading ? 0 : '48px',
          padding: isLoading ? 0 : '5px',
          transform: 'min-height 0.3s ease-in-out, padding 0.3s ease-in-out',
          overflow: 'hidden',
        }}
      >
        <p style={{ margin: 0, paddingLeft: theme.layout.spacing.small, fontSize: '20px' }}>
          There are{' '}
          <b>
            <u style={{ fontSize: '25px', padding: '5px' }}>{total || 'no'}</u>
          </b>{' '}
          {actionItem.lang.label} {search.length ? `matching '${search}'` : count == 0 ? 'today' : ''}
        </p>
      </Box>

      {haveData && (
        <DeliveryList
          className=""
          sx={{
            height: `calc(100vh - ${deliveryListOffsetTop}px)`,
            border: `2px solid ${actionItem.colors.foreground}`,
            borderBottomLeftRadius: theme.layout.spacing.small,
            borderBottomRightRadius: theme.layout.spacing.small,
            overflow: 'hidden',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: actionItem.colors.background,
              borderBottom: `2px solid ${actionItem.colors.foreground}`,
            },
          }}
          isActionItemsTable={true}
          data={deliveries?.result || []}
          numPages={1}
          curPage={1}
          pageSizeOptions={pageSizeOptions}
          pageSize={pageSize}
          isPastDeliveries={false}
          loading={isLoading}
          onRowClick={handleRowClick}
          tableType={actionItem.tableType}
        />
      )}

      {activeDeliveryForCheckInDeliveryDialog && (
        <CheckInDeliveryDialog
          deliveryId={activeDeliveryForCheckInDeliveryDialog.toString()}
          onClose={() => {
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
          onCancel={() => {
            // we still want to open the delivery but not the check in workflow
            navigate(Routes.CheckIn.path.replace(DELIVERY_ID_PARAM, activeDeliveryForCheckInDeliveryDialog.toString()));
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
        />
      )}
    </>
  );
};

export default DeliveriesActionItem;
